import { Box, Radio, Typography, useMediaQuery } from '@mui/material';
import {
  deserify,
  getLocalStorage,
  getTeacherSubjectEnum,
  getMediaBasePath,
  ImageWrapper,
  IStyles,
  Loader,
  pxToRem,
  pxTovW,
  SelectMenu,
  theme,
  useCommonServiceClientContext,
  QuantityButtonV2,
} from '@geneo2-web/shared-ui';
import {
  useAppDispatch,
  useAppSelector,
  useDeserifiedSelector,
} from 'apps/teacher/src/reduxStore/reduxHooks';
import { ChangeEvent, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  setClassAndSubjectInfo,
  setToastInfoV2,
} from '../../../Home/reducer/homeDashboard.slice';
import { ConnectError } from '@bufbuild/connect';
import {
  setAssessmentCreationType,
  setFaFormatDetails,
} from '../../reducer/assessmentFlow.slice';
import { SelectMultiSubjectsPopup } from '../../../ManageHomework/components/SelectMultiSubjectsPopup';
import {
  getSubjectsForClass,
  getSectionsForClass,
} from '../../../../utils/functions';
import {
  ASSESSMENT_CREATE,
  FA_DRAFT,
  FORMATIVE,
} from '../../../../routeHandling/RoutesNomenclature';
import {
  AssessmentEnum,
  MarkingSchemeEnum,
  ModeOfConductionEnum,
  MultiSubjectSection,
  MultiSubjectSectionStatusEnum,
  TaskCreationStatusEnum,
  TaskEnum,
} from '@protos/learning_management/lms.db_pb';
import { MultiSubjectFormateInfo } from '@protos/learning_management/lms.assessment.teacher.apis_pb';
import { Subject_SubjectEnum } from '@protos/common/common.db_pb';
import { buildTaskInfoModels } from '../../utils/QPGv2.utils';
import { DifficultyLevelEnum } from '@protos/content_management/content.db_pb';
import { MAXIMUM_MARKS_LIMIT } from '../../utils/assessment.utils';

const styles: IStyles = {
  root: {},
  lato27: {
    fontFamily: 'Lato',
    fontSize: { xs: pxToRem(15), md: pxTovW(27) },
  },
  subjectSelectBox: {
    borderRadius: { xs: pxToRem(10), md: pxTovW(30) },
    border: '1px solid #D2D2D2',
    paddingX: { xs: pxToRem(10), md: pxTovW(30) },
    paddingTop: { xs: pxToRem(10), md: pxTovW(15) },
    paddingBottom: { xs: pxToRem(10), md: pxTovW(15) },
    marginBottom: { xs: pxToRem(10), md: pxTovW(20) },
  },
  deleteSmall: {
    height: { xs: pxToRem(15), md: pxTovW(20) },
    width: { xs: pxToRem(15), md: pxTovW(20) },
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'flex-end',
    marginLeft: 'auto',
  },
  numericalInputField: {
    width: '100%',
    marginTop: { xs: pxToRem(10), md: pxTovW(18) },
    '& input': {
      width: '100%',
      height: { xs: pxToRem(40), md: pxTovW(74) },
      textAlign: 'center',
      borderRadius: { xs: pxToRem(15), md: pxTovW(15) },
    },
  },
  templateSelectMenu: {
    width: '100%',
    marginTop: { xs: pxToRem(10), md: pxTovW(18) },
    height: { xs: pxToRem(40), md: pxTovW(60) },
  },
  radioDetails: {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: pxToRem(10), md: pxTovW(7) },
  },
};

interface IProps {
  open: boolean;
  handleClose: () => void;
  onProceedClick?: () => void;
  sectionMetaInfo?: MultiSubjectSection[];
}
export const FASelectMultiSubjectsPopup = (props: IProps) => {
  const { open, handleClose, onProceedClick, sectionMetaInfo } = props;

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const teacherId = getLocalStorage('userId');
  const [searchParams] = useSearchParams();
  const assessmentId = searchParams.get('assessment_id');
  const { LmsAssessmentTeacherAPIServiceV6WithStatusCodeHandler } =
    useCommonServiceClientContext();
  const user_info = useDeserifiedSelector(
    (state) => state.auth.user_info
  );
  const class_subject_info = useDeserifiedSelector(
    (state) => state.homeDashboard.class_subject_info
  );
  const { manage_mode_of_conduction } = useAppSelector(
    (state) => state.manageHomework
  );
  const fa_format_details = useDeserifiedSelector(
    (state) => state.assessment.fa_format_details
  );
  const fa_content = useDeserifiedSelector(
    (state) => state.assessment.fa_content
  );

  const [loading, setLoading] = useState<'loading' | 'success' | 'error'>(
    'success'
  );
  const [classList, setClassList] = useState<{ name: string; id: number }[]>(
    []
  );

  const [radioValue, setRadioValue] = useState<'geneo' | 'jee' | 'custom'>(
    sectionMetaInfo?.[0]?.markingSchemeType ===
      MarkingSchemeEnum.MARKING_SCHEME_JEE
      ? 'jee'
      : 'geneo'
  );
  const [marksPerQuestion, setMarksPerQuestion] = useState(1);

  // * modifiedMetaInfo is used when we are modifying existing assessment
  const [modifiedMetaInfo, setModifiedMetaInfo] = useState<
    MultiSubjectSection[]
  >(sectionMetaInfo ?? []);

  // ----------------------------------------------------

  const getClassNamesAndClassIds = () => {
    const uniqueClassesMap = new Map();

    user_info?.teachClassSubjects?.forEach((classData) => {
      if (!uniqueClassesMap.has(classData.classId)) {
        uniqueClassesMap.set(classData.classId, {
          name: `Class ${classData.className}`,
          id: classData.classId,
        });
      }
    });

    return Array.from(uniqueClassesMap.values()).sort(
      (a, b) => Number(a.name.split(' ')[1]) - Number(b.name.split(' ')[1])
    );
  };

  // ----------------------------------------------------

  const handleClassChange = (value: string) => {
    const classData = classList.find((classData) => classData.name === value);
    dispatch(
      setFaFormatDetails({
        ...fa_format_details,
        selectedClass: classData,
        selectedSection: undefined,
        selectedSubjects: [{ name: '', id: 0 }],
      })
    );
  };

  // ----------------------------------------------------

  const handleSectionChange = (value: string) => {
    const sectionData = getSectionsForClass({
      classId: fa_format_details?.selectedClass?.id,
      userInfo: user_info,
    }).find((section) => section.name === value);
    dispatch(
      setFaFormatDetails({
        ...fa_format_details,
        selectedSection: sectionData,
      })
    );
  };

  // ----------------------------------------------------

  const popupCloseHandler = () => {
    handleClose();
    dispatch(
      setFaFormatDetails({
        ...fa_format_details,
        selectedSubjects: [{ name: '', id: 0 }],
      })
    );
    setModifiedMetaInfo([]);
    setRadioValue('geneo');
    setMarksPerQuestion(1);
  };

  // ----------------------------------------------------

  const handleAddMore = () => {
    if (idAddMoreDisabled()) return;
    const selectedSubjects = fa_format_details?.selectedSubjects ?? [];
    dispatch(
      setFaFormatDetails({
        ...fa_format_details,
        selectedSubjects: [...selectedSubjects, { name: '', id: 0 }],
      })
    );

    const newSectionMetaInfo = new MultiSubjectSection({
      instructions: [],
      avgDifficultyLevel: DifficultyLevelEnum.DIFFICULTY_LEVEL_UNDEFINED,
      multiSubjectSubSections: [],
      multiSubjectSectionStatus:
        MultiSubjectSectionStatusEnum.MULTI_SUBJECT_SECTION_STATUS_INITIALIZED,
      markingSchemeType: sectionMetaInfo?.[0]?.markingSchemeType,
    });
    const updatedMetaInfo = [
      ...modifiedMetaInfo,
      new MultiSubjectSection(newSectionMetaInfo),
    ].map(
      (item, index) =>
        new MultiSubjectSection({
          ...item,
          sectionNo: index + 1,
          multiSubjectSectionStatus: item.multiSubjectSectionStatus
            ? item.multiSubjectSectionStatus
            : MultiSubjectSectionStatusEnum.MULTI_SUBJECT_SECTION_STATUS_INITIALIZED,
        })
    );
    setModifiedMetaInfo(updatedMetaInfo);
  };

  // ----------------------------------------------------

  const controlRadioProps = (item: string) => ({
    name: 'color-radio-button',
    value: item,
    checked: radioValue === item,
    onChange: async (event: ChangeEvent<HTMLInputElement>) => {
      setRadioValue(event.target.value as 'geneo' | 'jee' | 'custom');
    },
  });

  // ----------------------------------------------------

  const handleRemoveSection = (deleteIndex: number) => {
    let updatedSubjects = fa_format_details?.selectedSubjects?.filter(
      (item, itemIndex) => itemIndex !== deleteIndex
    );
    if (!updatedSubjects || updatedSubjects.length === 0) {
      updatedSubjects = [{ name: '', id: 0 }];
    }
    dispatch(
      setFaFormatDetails({
        ...fa_format_details,
        selectedSubjects: updatedSubjects,
      })
    );

    const updatedMetaInfo = modifiedMetaInfo
      .filter((_, index) => index !== deleteIndex)
      .map(
        (item, index) =>
          new MultiSubjectSection({ ...item, sectionNo: index + 1 })
      );
    setModifiedMetaInfo(updatedMetaInfo);
  };

  // ----------------------------------------------------

  const isSectionRowLocked = (index: number) => {
    if (!sectionMetaInfo?.length) return false;
    const status = modifiedMetaInfo[index]?.multiSubjectSectionStatus;
    return (
      status !== undefined &&
      status !==
        MultiSubjectSectionStatusEnum.MULTI_SUBJECT_SECTION_STATUS_UNDEFINED &&
      status !==
        MultiSubjectSectionStatusEnum.MULTI_SUBJECT_SECTION_STATUS_INITIALIZED
    );
  };

  // ----------------------------------------------------

  const handleSubjectChange = (
    subjectValue: string,
    subjectId: number,
    index: number
  ) => {
    const updatedSubjects = [...(fa_format_details?.selectedSubjects ?? [])];
    updatedSubjects[index] = { name: subjectValue, id: subjectId };
    dispatch(
      setFaFormatDetails({
        ...fa_format_details,
        selectedSubjects: updatedSubjects,
      })
    );

    const subjectEnum =
      getTeacherSubjectEnum(subjectId, user_info?.teachClassSubjects) ??
      Subject_SubjectEnum.UNDEFINED;
    const updatedMetaInfo = [...modifiedMetaInfo];
    const existing = updatedMetaInfo[index];
    updatedMetaInfo[index] = new MultiSubjectSection({
      ...existing,
      subjectId: subjectId,
      subject: subjectEnum,
    });
    setModifiedMetaInfo(updatedMetaInfo);
  };

  // ----------------------------------------------------

  const idAddMoreDisabled = () => {
    const selectedSubjects = fa_format_details?.selectedSubjects ?? [];
    if (selectedSubjects.length === 0) return false;
    return (
      selectedSubjects[selectedSubjects.length - 1]?.id === 0 ||
      selectedSubjects.length >= 20
    );
  };

  // ----------------------------------------------------

  const isProceedButtonDisabled = () => {
    return (
      !fa_format_details?.selectedClass ||
      !fa_format_details?.selectedSection?.id ||
      !fa_format_details?.assessmentName?.trim() ||
      (fa_format_details?.selectedSubjects ?? []).some(
        (subject) => subject?.id === 0
      )
    );
  };

  // ----------------------------------------------------

  const getSubjectIds = (): number[] | undefined => {
    return fa_format_details?.selectedSubjects
      ?.map((item) => item.id)
      ?.filter((item): item is number => item !== undefined);
  };

  // ----------------------------------------------------

  const handleProceedClick = async () => {
    if (fa_format_details?.selectedSubjects?.length === 1) {
      if (fa_format_details.selectedClass?.id === class_subject_info?.classId) {
        const selectedSubjectId = fa_format_details?.selectedSubjects?.[0]?.id;
        const classSections = user_info?.teachClassSubjects?.filter(
          (s) => s.classId === fa_format_details?.selectedClass?.id
        );
        let targetClassSection = classSections?.find(
          (s) => s.sectionId === class_subject_info?.sectionId
        );

        if (
          !targetClassSection ||
          !targetClassSection.subjects?.some(
            (subject) => subject.subjectId === selectedSubjectId
          )
        ) {
          targetClassSection = classSections?.find((section) =>
            section.subjects?.some(
              (subject) => subject.subjectId === selectedSubjectId
            )
          );
        }

        const matchedSubject = targetClassSection?.subjects?.find(
          (s) => s.subjectId === selectedSubjectId
        );

        if (matchedSubject) {
          dispatch(
            setClassAndSubjectInfo({
              classname: targetClassSection?.className,
              section: targetClassSection?.sectionName,
              sectionId: targetClassSection?.sectionId,
              subjectId: matchedSubject?.subjectId,
              subject: matchedSubject?.subjectName,
              icon: matchedSubject?.iconUrl
                ? getMediaBasePath(
                    matchedSubject.iconUrl,
                    'schoolnetStaticAssetBucket'
                  )
                : '',
              color: matchedSubject?.textColor,
              bookId: matchedSubject?.bookId,
              classId: targetClassSection?.classId,
              isClickerSupported: !!targetClassSection?.isClickerSupported,
            })
          );
        }
      } else {
        const selectedSubjectId = fa_format_details?.selectedSubjects?.[0]?.id;
        const targetClassSection = user_info?.teachClassSubjects?.find(
          (section) =>
            section.classId === fa_format_details?.selectedClass?.id &&
            section.subjects?.some(
              (subject) => subject.subjectId === selectedSubjectId
            )
        );

        const matchedSubject = targetClassSection?.subjects?.find(
          (s) => s.subjectId === selectedSubjectId
        );

        if (matchedSubject) {
          dispatch(
            setClassAndSubjectInfo({
              classname: targetClassSection?.className,
              section: targetClassSection?.sectionName,
              sectionId: targetClassSection?.sectionId,
              subjectId: matchedSubject?.subjectId,
              subject: matchedSubject?.subjectName,
              icon: matchedSubject?.iconUrl
                ? getMediaBasePath(
                    matchedSubject.iconUrl,
                    'schoolnetStaticAssetBucket'
                  )
                : '',
              color: matchedSubject?.textColor,
              bookId: matchedSubject?.bookId,
              classId: targetClassSection?.classId,
              isClickerSupported: !!targetClassSection?.isClickerSupported,
            })
          );
        }
      }
      dispatch(
        setAssessmentCreationType({
          asessmentType: AssessmentEnum.ASSESSMENT_FORMATIVE_ASSESSMENT,
          modeOfConduction: manage_mode_of_conduction,
          creationType: 'Create',
          markingScheme:
            radioValue === 'custom'
              ? MarkingSchemeEnum.MARKING_SCHEME_CUSTOM
              : radioValue === 'jee'
              ? MarkingSchemeEnum.MARKING_SCHEME_JEE
              : MarkingSchemeEnum.MARKING_SCHEME_GENEO,
          assessmentName: fa_format_details?.assessmentName,
          marksPerQuestion:
            radioValue === 'custom' ? marksPerQuestion : undefined,
        })
      );

      navigate(
        `${FORMATIVE}${ASSESSMENT_CREATE}/${fa_format_details?.selectedSubjects[0].id}`
      );
      return;
    }

    let multiSubjectFormateInfo: MultiSubjectFormateInfo[] | undefined;
    if (!sectionMetaInfo) {
      const selectedSubjects = fa_format_details?.selectedSubjects ?? [];
      multiSubjectFormateInfo = selectedSubjects
        .filter((subject) => subject.id != null && subject.id !== 0)
        .map((subject, index) => {
          const subjectEnum =
            getTeacherSubjectEnum(subject.id, user_info?.teachClassSubjects) ??
            Subject_SubjectEnum.UNDEFINED;

          return new MultiSubjectFormateInfo({
            subjectId: subject.id,
            subject: subjectEnum,
            sectionNo: index + 1,
            markingSchemeType:
              radioValue === 'custom'
                ? MarkingSchemeEnum.MARKING_SCHEME_CUSTOM
                : radioValue === 'jee'
                ? MarkingSchemeEnum.MARKING_SCHEME_JEE
                : MarkingSchemeEnum.MARKING_SCHEME_GENEO,
          });
        });
    }

    try {
      setLoading('loading');
      const assessmentCreationSubmitResponse =
        await LmsAssessmentTeacherAPIServiceV6WithStatusCodeHandler.assessmentCreationSubmit(
          {
            teacherId: BigInt(teacherId),
            assessmentId: (assessmentId ? BigInt(assessmentId) : undefined),
            classId: fa_format_details?.selectedClass?.id,
            creationStatus: TaskCreationStatusEnum.TASK_CREATION_STATUS_DRAFT,
            multiSubjectFormateInfo: sectionMetaInfo
              ? undefined
              : multiSubjectFormateInfo,
            multiSubjectSectionMetaInfo: sectionMetaInfo
              ? modifiedMetaInfo
              : undefined,
            sectionId:
              fa_format_details?.selectedSection?.id ||
              class_subject_info?.sectionId,
            subjectIds: getSubjectIds(),
            taskName: fa_format_details?.assessmentName,
            taskType: TaskEnum.TASK_MULTI_SUBJECT_FORMATIVE_ASSESSMENT_CUSTOM,
            instructions: sectionMetaInfo
              ? fa_content?.assessment?.assessmentInstructions
              : undefined,
            questionModuleMap:
              fa_content?.questionsSequenceInfo?.questionModuleMap,
            selectedTasksInfo: sectionMetaInfo
              ? buildTaskInfoModels(modifiedMetaInfo)
              : undefined,
            modeOfConduction: manage_mode_of_conduction,
            markingSchemeType:
              radioValue === 'custom'
                ? MarkingSchemeEnum.MARKING_SCHEME_CUSTOM
                : radioValue === 'jee'
                ? MarkingSchemeEnum.MARKING_SCHEME_JEE
                : MarkingSchemeEnum.MARKING_SCHEME_GENEO,
            marksPerQuestion:
              radioValue === 'custom' ? marksPerQuestion : undefined,
          }
        );

      if (assessmentCreationSubmitResponse.status === 200) {
        navigate(
          FA_DRAFT +
            `?assessment_id=${assessmentCreationSubmitResponse.data?.assessment?.assessmentId}`
        );
        onProceedClick?.();
        setLoading('success');
      } else {
        dispatch(
          setToastInfoV2({
            label: 'Error while creating assessment',
            variant: 'error',
            open: true,
          })
        );
        setLoading('error');
      }
    } catch (error) {
      console.log('error:', error);
      setLoading('error');
      let message = 'Error while creating assessment';
      if (error instanceof ConnectError) {
        message = error.rawMessage?.replace(/.*\]\s*/, '').trim();
      }
      dispatch(
        setToastInfoV2({
          label: message,
          variant: 'error',
          open: true,
        })
      );
    }
  };

  // ----------------------------------------------------

  useEffect(() => {
    setClassList(getClassNamesAndClassIds());
  }, [user_info?.teachClassSubjects]);

  useEffect(() => {
    if (open && class_subject_info) {
      const hasValidSubjects =
        (fa_format_details?.selectedSubjects?.length ?? 0) > 0 &&
        fa_format_details?.selectedSubjects?.some(
          (s) => s.id != null && s.id !== 0
        );

      dispatch(
        setFaFormatDetails({
          ...fa_format_details,
          selectedClass: {
            id: class_subject_info.classId,
            name: `Class ${class_subject_info.classname}`,
          },
          selectedSection: {
            id: class_subject_info.sectionId,
            name: class_subject_info.section,
          },
          ...(!hasValidSubjects && {
            selectedSubjects: [
              {
                id: class_subject_info.subjectId,
                name: class_subject_info.subject,
              },
            ],
          }),
        })
      );
    }
  }, [open, class_subject_info?.classId]);

  // ----------------------------------------------------

  // useEffect(() => {
  //   if (class_subject_info) {
  //     const hasValidSubjects =
  //       (fa_format_details?.selectedSubjects?.length ?? 0) > 0 &&
  //       fa_format_details?.selectedSubjects?.some(
  //         (s) => s.id != null && s.id !== 0
  //       );
  //     if (!hasValidSubjects) {
  //       dispatch(
  //         setFaFormatDetails({
  //           ...fa_format_details,
  //           selectedClass: {
  //             id: class_subject_info.classId,
  //             name: `Class ${class_subject_info.classname}`,
  //           },
  //         })
  //       );
  //     }
  //   }
  // }, [JSON.stringify(class_subject_info)]);

  // ----------------------------------------------------

  if (loading === 'loading') return <Loader />;

  return (
    <SelectMultiSubjectsPopup
      popupTitle="Create Your Formative Assessment"
      popupOpen={open}
      popupCloseHandler={popupCloseHandler}
      chooseClassData={{
        selectedClass: fa_format_details?.selectedClass?.name ?? '',
        classList: classList,
        handleClassChange: handleClassChange,
        disabled: !!sectionMetaInfo,
      }}
      chooseSectionData={{
        selectedSection: fa_format_details?.selectedSection?.name ?? '',
        sectionList: getSectionsForClass({
          classId: fa_format_details?.selectedClass?.id,
          userInfo: user_info,
        }),
        handleSectionChange: handleSectionChange,
        disabled: !fa_format_details?.selectedClass,
      }}
      nameChangeData={{
        title: 'Formative Assessment Name',
        name: 'assessmentName',
        value: fa_format_details?.assessmentName ?? '',
        onChange: (e: ChangeEvent<HTMLInputElement>) => {
          dispatch(
            setFaFormatDetails({
              ...fa_format_details,
              assessmentName: e.target.value,
            })
          );
        },
      }}
      radioData={
        manage_mode_of_conduction !==
        ModeOfConductionEnum.MODE_OF_CONDUCTION_GENEO_CLICKER
          ? [
              {
                title: 'Geneo Marking',
                value: 'geneo',
                disabled: !!sectionMetaInfo,
                controlRadioProps,
              },
              {
                title: 'JEE Marking',
                value: 'jee',
                disabled: !!sectionMetaInfo,
                controlRadioProps,
              },
              {
                title: 'Custom Marking',
                value: 'custom',
                disabled: !!sectionMetaInfo,
                controlRadioProps,
              },
            ]
          : undefined
      }
      radioExtraNode={
        manage_mode_of_conduction !==
        ModeOfConductionEnum.MODE_OF_CONDUCTION_GENEO_CLICKER ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              marginRight: pxToRem(20),
            }}
          >
            {radioValue === 'custom' && (
              <Box sx={{ mt: 2 }}>
                <QuantityButtonV2
                  value={marksPerQuestion}
                  max={MAXIMUM_MARKS_LIMIT}
                  // disabled={maxQuantityFromSelectedTypes === 0}
                  onChange={async (nextValue: number) => {
                    setMarksPerQuestion(nextValue);
                  }}
                  onIncrement={async () => {
                    setMarksPerQuestion((prev) =>
                      Math.min(prev + 1, MAXIMUM_MARKS_LIMIT)
                    );
                  }}
                  onDecrement={async () => {
                    setMarksPerQuestion((prev) => Math.max(prev - 1, 1));
                  }}
                />
              </Box>
            )}
          </Box>
        ) : undefined
      }
      sectionInputNode={
        <Box>
          {(fa_format_details?.selectedSubjects ?? [{ name: '', id: 0 }]).map(
            (subject, index) => (
              <Box key={index} sx={styles.subjectSelectBox}>
                {!isSectionRowLocked(index) &&
                  !(
                    subject.name === '' &&
                    (fa_format_details?.selectedSubjects?.length ?? 1) === 1
                  ) && (
                    <ImageWrapper
                      name="delete-small"
                      parentFolder="icons"
                      type="png"
                      onClick={() => {
                        handleRemoveSection(index);
                      }}
                      styles={styles.deleteSmall}
                    />
                  )}

                <Box>
                  <Typography variant="bodyText" sx={styles.lato27}>
                    Choose Subject
                  </Typography>
                  <SelectMenu
                    disabled={
                      !fa_format_details?.selectedClass ||
                      isSectionRowLocked(index)
                    }
                    value={subject.name ?? ''}
                    optionList={getSubjectsForClass({
                      classId: fa_format_details?.selectedClass?.id,
                      userInfo: user_info,
                    })}
                    onChange={(subjectValue: string) => {
                      const subjectId = getSubjectsForClass({
                        classId: fa_format_details?.selectedClass?.id,
                        userInfo: user_info,
                      }).find((item) => item.name === subjectValue)?.id;
                      if (!subjectId) return;
                      handleSubjectChange(subjectValue, subjectId, index);
                    }}
                    rootStyles={{
                      width: '100%',
                      marginTop: { xs: pxToRem(10), md: pxTovW(18) },
                      height: { xs: pxToRem(40), md: pxTovW(60) },
                    }}
                  />
                </Box>
              </Box>
            )
          )}
        </Box>
      }
      addMoreSectionData={{
        idAddMoreDisabled: idAddMoreDisabled(),
        handleAddMore: handleAddMore,
      }}
      proceedButtonData={{
        disabled: isProceedButtonDisabled(),
        onClick: handleProceedClick,
      }}
    />
  );
};
